import React, { useState, useEffect } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    height: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 4px;
    border: 1px solid transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #4b5563;
  }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #9ca3af;
  }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #d1d5db;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = scrollbarStyles;
  document.head.appendChild(styleSheet);
}

const ProgressGraph = ({ subjectId, onRetestClick }) => {
  const [examSessions, setExamSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [error, setError] = useState(null);
  const [hoveredSession, setHoveredSession] = useState(null);
  const scrollContainerRef = React.useRef(null);

  useEffect(() => {
    fetchExamSessions();
  }, [subjectId]);

  // Auto-scroll to show latest exams when data loads
  useEffect(() => {
    if (examSessions.length > 8 && scrollContainerRef.current) {
      // Scroll to the right to show latest exams
      setTimeout(() => {
        scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth;
      }, 100);
    }
  }, [examSessions]);

  const fetchExamSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/exams/subjects/${subjectId}/exam-sessions/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Data should now be an array of finished sessions for this subject
        const completedSessions = data.filter(session => 
          (session.status === 'completed' || session.status === 'submitted') && 
          session.score !== null && session.score >= 0 &&
          (session.completed_at || session.created_at)
        ).sort((a, b) => new Date(a.completed_at || a.created_at) - new Date(b.completed_at || b.created_at));
        
        setExamSessions(completedSessions);
      } else {
        setError('Failed to fetch exam sessions');
      }
    } catch (error) {
      setError('Error loading progress data');
    } finally {
      setLoading(false);
    }
  };

  const handleRetestSession = () => {
    if (selectedSession && onRetestClick) {
      onRetestClick(selectedSession);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getNodeColor = (score, isSelected, isHovered) => {
    let baseColor, selectedColor, hoveredColor;
    
    if (score >= 80) {
      baseColor = '#10b981'; // green-500
      selectedColor = '#059669'; // green-600
      hoveredColor = '#047857'; // green-700
    } else if (score >= 60) {
      baseColor = '#f59e0b'; // yellow-500
      selectedColor = '#d97706'; // yellow-600
      hoveredColor = '#b45309'; // yellow-700
    } else {
      baseColor = '#ef4444'; // red-500
      selectedColor = '#dc2626'; // red-600
      hoveredColor = '#b91c1c'; // red-700
    }

    if (isSelected) return selectedColor;
    if (isHovered) return hoveredColor;
    return baseColor;
  };

  const getNodeSize = (isSelected, isHovered) => {
    if (isSelected) return 10;
    if (isHovered) return 8;
    return 6;
  };

  const getTooltipPosition = (nodeX, nodeY, graphWidth, graphHeight) => {
    const tooltipWidth = 120;
    const tooltipHeight = 60;
    const offset = 15;
    
    // Default to top-right
    let x = nodeX + offset;
    let y = nodeY - tooltipHeight - offset;
    
    // Adjust if tooltip goes off screen
    if (x + tooltipWidth > graphWidth - 10) {
      x = nodeX - tooltipWidth - offset; // Move to left
    }
    if (y < 10) {
      y = nodeY + offset; // Move below node
    }
    
    return { x, y };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button 
          onClick={fetchExamSessions}
          className="mt-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Try again
        </button>
      </div>
    );
  }

  if (examSessions.length === 0) {
    return (
      <div className="text-center py-8">
        <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Completed Tests</h4>
        <p className="text-gray-500 dark:text-gray-400">
          Complete some tests to see your progress over time.
        </p>
      </div>
    );
  }

  // Calculate graph dimensions for responsive layout
  const graphHeight = 320; // Increased height for better visibility
  const padding = { top: 30, right: 20, bottom: 50, left: 40 }; // Optimized padding
  const plotHeight = graphHeight - (padding.top + padding.bottom);
  
  // Calculate dynamic width based on number of sessions
  const sessionSpacing = 80; // Equal spacing between sessions
  const minGraphWidth = 800; // Minimum width for readability
  const calculatedWidth = Math.max(minGraphWidth, (examSessions.length * sessionSpacing) + padding.left + padding.right);
  const graphWidth = calculatedWidth;
  const plotWidth = graphWidth - (padding.left + padding.right);

  // Sort all sessions chronologically (oldest to newest)
  const sortedSessions = [...examSessions].sort((a, b) => 
    new Date(a.completed_at || a.created_at) - new Date(b.completed_at || b.created_at)
  );

  // Calculate positions with equal horizontal spacing
  const sessionPoints = sortedSessions.map((session, index) => {
    // Equal spacing regardless of time
    const x = padding.left + (index * sessionSpacing) + (sessionSpacing / 2);
    const y = padding.top + (100 - session.score) * (plotHeight / 100);
    
    return {
      ...session,
      x: x,
      y: Math.max(padding.top, Math.min(graphHeight - padding.bottom, y)),
      index: index
    };
  });

  return (
    <div className="w-full flex flex-col items-center">
      {/* Scroll indicator */}
      {examSessions.length > 8 && (
        <div className="text-center text-xs text-gray-500 dark:text-gray-400 mb-2">
          Showing {examSessions.length} exams • Scroll horizontally to see older tests
        </div>
      )}
      
      {/* Scrollable Graph Container - Fully Responsive */}
      <div 
        ref={scrollContainerRef}
        className="relative bg-gray-50 dark:bg-gray-700/50 rounded-lg p-1 overflow-x-auto overflow-y-hidden w-full custom-scrollbar"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#6b7280 transparent'
        }}
      >
        <svg width={graphWidth} height={graphHeight} className="block" style={{minWidth: `${minGraphWidth}px`}}>
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="40" height="30" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 30" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-300 dark:text-gray-600" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width={graphWidth} height={graphHeight} fill="url(#grid)" />
          
          {/* Y-axis labels (scores) */}
          {[0, 20, 40, 60, 80, 100].map(score => (
            <g key={score}>
              <line 
                x1={padding.left} 
                y1={padding.top + (100 - score) * (plotHeight / 100)} 
                x2={graphWidth - padding.right} 
                y2={padding.top + (100 - score) * (plotHeight / 100)}
                stroke="currentColor" 
                strokeWidth="1" 
                className="text-gray-300 dark:text-gray-600" 
                opacity="0.3"
              />
              <text 
                x={padding.left - 10} 
                y={padding.top + (100 - score) * (plotHeight / 100) + 5} 
                textAnchor="end" 
                className="text-xs fill-gray-600 dark:fill-gray-400"
              >
                {score}%
              </text>
            </g>
          ))}

          {/* X-axis */}
          <line 
            x1={padding.left} 
            y1={graphHeight - padding.bottom} 
            x2={graphWidth - padding.right} 
            y2={graphHeight - padding.bottom} 
            stroke="currentColor" 
            strokeWidth="2" 
            className="text-gray-400 dark:text-gray-500"
          />

          {/* Y-axis */}
          <line 
            x1={padding.left} 
            y1={padding.top} 
            x2={padding.left} 
            y2={graphHeight - padding.bottom} 
            stroke="currentColor" 
            strokeWidth="2" 
            className="text-gray-400 dark:text-gray-500"
          />

          {/* Date labels on X-axis - grouped for same day */}
          {(() => {
            const dateGroups = {};
            sessionPoints.forEach((point, index) => {
              const date = new Date(point.completed_at || point.created_at);
              const dateKey = date.toDateString();
              if (!dateGroups[dateKey]) {
                dateGroups[dateKey] = [];
              }
              dateGroups[dateKey].push({ ...point, index });
            });

            return Object.entries(dateGroups).map(([dateKey, points]) => {
              const date = new Date(dateKey);
              const dateLabel = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric'
              });
              
              // Use the middle position for grouped dates
              const avgX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
              
              return (
                <g key={dateKey}>
                  <line 
                    x1={avgX} 
                    y1={graphHeight - padding.bottom} 
                    x2={avgX} 
                    y2={graphHeight - padding.bottom + 5}
                    stroke="currentColor" 
                    strokeWidth="1" 
                    className="text-gray-400 dark:text-gray-500"
                  />
                  <text
                    x={avgX}
                    y={graphHeight - padding.bottom + 20}
                    textAnchor="middle"
                    className="text-xs fill-gray-600 dark:fill-gray-400"
                  >
                    {dateLabel}
                  </text>
                </g>
              );
            });
          })()}

          {/* Connect points with lines */}
          {sessionPoints.length > 1 && (
            <polyline
              points={sessionPoints.map(point => `${point.x},${point.y}`).join(' ')}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-blue-500 dark:text-blue-400"
            />
          )}

          {/* Data points with enhanced tooltips */}
          {sessionPoints.map((point) => {
            const sessionDate = new Date(point.completed_at || point.created_at);
            const dateStr = sessionDate.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: sessionDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
            });
            const timeStr = sessionDate.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false
            });
            
            const isSelected = selectedSession?.id === point.id;
            const isHovered = hoveredSession?.id === point.id;
            const tooltipPos = getTooltipPosition(point.x, point.y, graphWidth, graphHeight);
            
            return (
              <g key={point.id}>
                {/* Selection ring for selected nodes */}
                {isSelected && (
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="14"
                    fill="none"
                    stroke={getNodeColor(point.score, true, false)}
                    strokeWidth="2"
                    opacity="0.6"
                    className="animate-pulse"
                  />
                )}
                
                {/* Simple Modern Tooltip */}
                {isSelected && (
                  <g>
                    {/* Tooltip shadow */}
                    <rect
                      x={tooltipPos.x + 2}
                      y={tooltipPos.y + 2}
                      width="120"
                      height="60"
                      rx="6"
                      fill="rgba(0,0,0,0.15)"
                    />
                    
                    {/* Tooltip background */}
                    <rect
                      x={tooltipPos.x}
                      y={tooltipPos.y}
                      width="120"
                      height="60"
                      rx="6"
                      fill="white"
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      className="dark:fill-gray-800 dark:stroke-gray-600"
                    />
                    
                    {/* Date */}
                    <text
                      x={tooltipPos.x + 60}
                      y={tooltipPos.y + 16}
                      textAnchor="middle"
                      fill="#6b7280"
                      className="dark:fill-gray-300"
                      fontSize="11"
                      fontWeight="500"
                    >
                      {dateStr}
                    </text>
                    
                    {/* Score - large and prominent */}
                    <text
                      x={tooltipPos.x + 60}
                      y={tooltipPos.y + 35}
                      textAnchor="middle"
                      fill={getNodeColor(point.score, false, false)}
                      fontSize="20"
                      fontWeight="700"
                    >
                      {point.score.toFixed(1)}%
                    </text>
                    
                    {/* Re-test button - compact */}
                    <rect
                      x={tooltipPos.x + 10}
                      y={tooltipPos.y + 42}
                      width="100"
                      height="14"
                      rx="7"
                      fill="#3b82f6"
                      className="cursor-pointer hover:fill-blue-600 transition-colors"
                      onClick={() => onRetestClick(point)}
                    />
                    <text
                      x={tooltipPos.x + 60}
                      y={tooltipPos.y + 51}
                      textAnchor="middle"
                      fill="white"
                      fontSize="10"
                      fontWeight="600"
                      className="cursor-pointer"
                      onClick={() => onRetestClick(point)}
                    >
                      Re-test
                    </text>
                  </g>
                )}
                
                {/* Data point circle with enhanced states */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={getNodeSize(isSelected, isHovered)}
                  fill={getNodeColor(point.score, isSelected, isHovered)}
                  stroke={isSelected ? "white" : "none"}
                  strokeWidth={isSelected ? "2" : "0"}
                  className="cursor-pointer transition-all duration-200 drop-shadow-sm"
                  onClick={() => setSelectedSession(isSelected ? null : point)}
                  onMouseEnter={() => setHoveredSession(point)}
                  onMouseLeave={() => setHoveredSession(null)}
                />
                
              </g>
            );
          })}

        </svg>
      </div>
      
      {/* Instructions */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
        Click on any point to see test details and re-test option
      </div>
    </div>
  );
};

export default ProgressGraph;
